/**
 * @page FlexPoolBestaetigung
 * @route /flex/pool-bestaetigung/:token (public, no auth)
 * @description Public Pool-Bestätigungsseite. Mobile-optimiert.
 *   Token-basiert: Bewerber klickt Link aus E-Mail → bestätigt Pool-Aufnahme.
 */
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

const API = import.meta.env.VITE_API_URL || '';

export default function FlexPoolBestaetigung() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading'); // loading, ready, confirmed, expired, invalid, error
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/public/flex/pool-bestaetigung/${token}`);
        if (res.status === 404) { setStatus('invalid'); return; }
        if (!res.ok) { setStatus('error'); return; }
        const d = await res.json();
        setData(d);
        if (d.status === 'already_confirmed') setStatus('confirmed');
        else if (d.status === 'expired') setStatus('expired');
        else setStatus('ready');
      } catch { setStatus('error'); }
    })();
  }, [token]);

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      const res = await fetch(`${API}/api/public/flex/pool-bestaetigung/${token}`, { method: 'POST' });
      if (res.ok) {
        const d = await res.json();
        setStatus('confirmed');
      } else if (res.status === 410) {
        setStatus('expired');
      } else {
        setStatus('error');
      }
    } catch { setStatus('error'); }
    finally { setConfirming(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F4F5FA] to-white flex flex-col">
      {/* Logo bar */}
      <div className="w-full px-6 py-5 flex items-center justify-center">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#338edc] to-[#6366F1] flex items-center justify-center">
            <span className="text-white font-bold text-sm">W</span>
          </div>
          <span className="text-[15px] font-semibold text-[#1A1A2E]">WeShip</span>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-start justify-center px-4 pb-12">
        <div className="w-full max-w-md">

          {/* Loading */}
          {status === 'loading' && (
            <div className="text-center py-20 text-[#9CA3AF]">...</div>
          )}

          {/* Invalid token */}
          {status === 'invalid' && (
            <div className="bg-white rounded-2xl border border-[var(--border-default,#E5E7EB)] p-8 text-center">
              <div className="text-4xl mb-4">&#128279;</div>
              <h2 className="text-lg font-semibold text-[#1A1A2E] mb-2">Dieser Link ist nicht mehr gültig.</h2>
              <p className="text-sm text-[#6B7280]">Bei Fragen: jobs@email.weship.eu</p>
            </div>
          )}

          {/* Expired */}
          {status === 'expired' && (
            <div className="bg-white rounded-2xl border border-[var(--border-default,#E5E7EB)] p-8 text-center">
              <div className="text-4xl mb-4">&#9203;</div>
              <h2 className="text-lg font-semibold text-[#1A1A2E] mb-2">Dieser Link ist abgelaufen.</h2>
              <p className="text-sm text-[#6B7280]">Bitte kontaktiere uns unter jobs@email.weship.eu</p>
            </div>
          )}

          {/* Error */}
          {status === 'error' && (
            <div className="bg-white rounded-2xl border border-[var(--border-default,#E5E7EB)] p-8 text-center">
              <h2 className="text-lg font-semibold text-[#1A1A2E] mb-2">Ein Fehler ist aufgetreten.</h2>
              <p className="text-sm text-[#6B7280]">Bitte versuche es später erneut oder kontaktiere jobs@email.weship.eu</p>
            </div>
          )}

          {/* Ready */}
          {status === 'ready' && data && (
            <div className="bg-white rounded-2xl border border-[var(--border-default,#E5E7EB)] overflow-hidden">
              <div className="px-6 pt-6 pb-4">
                <div className="text-4xl mb-4 text-center">&#127881;</div>
                <h1 className="text-xl font-semibold text-[#1A1A2E] text-center">
                  Herzlichen Glückwunsch{data.bewerber_name ? `, ${data.bewerber_name.split(' ')[0]}` : ''}!
                </h1>
                <p className="mt-3 text-sm text-[#6B7280] text-center leading-relaxed">
                  Du wurdest in den <strong className="text-[#1A1A2E]">WeShip Flex-Pool</strong> aufgenommen.
                  Ab sofort wirst du per E-Mail und SMS informiert wenn wir Unterstützung brauchen.
                </p>
              </div>
              <div className="px-6 pb-6">
                <button
                  onClick={handleConfirm}
                  disabled={confirming}
                  className="w-full py-4 rounded-xl bg-[#338edc] hover:bg-[#2b7bc4] text-white font-semibold text-base disabled:opacity-50 transition-colors"
                >
                  {confirming ? '...' : 'Aufnahme bestätigen'}
                </button>
              </div>
            </div>
          )}

          {/* Confirmed */}
          {status === 'confirmed' && (
            <div className="bg-white rounded-2xl border border-[var(--border-default,#E5E7EB)] p-8 text-center">
              <div className="text-4xl mb-4">&#9989;</div>
              <h2 className="text-lg font-semibold text-[#065F46] mb-2">Willkommen im Flex-Pool!</h2>
              <p className="text-sm text-[#047857]">
                Deine Aufnahme ist bestätigt. Du erhältst bald Einsatz-Anfragen per E-Mail und SMS.
              </p>
            </div>
          )}

          {/* Footer */}
          <div className="text-center mt-8 text-xs text-[#9CA3AF] space-y-1">
            <p>Fragen? jobs@email.weship.eu</p>
            <p>&copy; 2026 We Ship Fulfillment GmbH</p>
          </div>
        </div>
      </div>
    </div>
  );
}
